/**
 * Messages (design/prototype/Messages.dc.html, Inbox.dc.html): every text and
 * email with a person, as one conversation per person — newest first, unread
 * ones marked — and a conversation laid out by day. Staff read English first:
 * a cleaner's text sent in French shows its English. Pure.
 */
import { clockAt, shortDay, wallClock, weekdayDay } from '../shared/zoned.ts'
import type { Communication } from './communication.ts'

/**
 * The list's groups. System isn't a person's conversation: it is one row per
 * location or cleaner with messages to or from no one (system-inbox.ts).
 */
export const threadGroups = ['all', 'client', 'cleaner', 'unread', 'system'] as const
export type ThreadGroup = (typeof threadGroups)[number]
/** The groups that list people's conversations. */
export type PersonGroup = Exclude<ThreadGroup, 'system'>

export const threadGroupLabels: Record<ThreadGroup, string> = {
  all: 'All',
  client: 'Clients',
  cleaner: 'Cleaners',
  unread: 'Unread',
  system: 'System',
}

/** How many conversations each group holds, counted in the database. */
export type ThreadCounts = Record<ThreadGroup, number>

/** Who a person is to Majik — a client's contact or a cleaner — for the list's second line. */
export interface ThreadPerson {
  contactId: string
  name: string
  kind: 'client' | 'cleaner'
  /** "Another Tomorow", or "Cleaner · nighttime · French". */
  context: string
  shift?: 'daytime' | 'nighttime'
}

export interface Thread extends ThreadPerson {
  initials: string
  when: string
  /** "You: Sure, I'll let her know." / "Auto: Hey Georgia…" / the reply itself. */
  preview: string
  unread: boolean
  /** Inbound messages nobody has read yet. */
  unreadCount: number
  at: string
}

/** How a message we sent got on: written while Go live is off, sent, or failed. */
export type Delivery = 'would_send' | 'sent' | 'failed'

export const deliveryOf = (status: Communication['status']): Delivery | null =>
  status === 'would_send' || status === 'sent' || status === 'failed' ? status : null

export interface ConversationMessage {
  id: string
  kind: 'text' | 'email'
  direction: 'inbound' | 'outbound'
  subject: string | null
  text: string
  /** "9:41 AM". */
  time: string
  /** When, as an instant — messages close together share one name-and-time line. */
  at: string
  channel: 'Text' | 'Email'
  /** Who wrote an outbound message: a staff name, or "Automatic". Null for one that came in. */
  by: string | null
  /** Null for a message that came in. */
  delivery: Delivery | null
  /** One of a blast to many cleaners: the thread marks it. */
  blast: boolean
  /** Came in and nobody has read it yet. */
  unread: boolean
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const DAY = 86_400_000

/** "Today", "Yesterday", "Wed, Oct 21" — and the year too, before this one. */
export function dayLabel(at: string, now: string, tz: string): string {
  const a = wallClock(at, tz)
  const n = wallClock(now, tz)
  const days = Math.round(
    (Date.UTC(n.year, n.month - 1, n.day) - Date.UTC(a.year, a.month - 1, a.day)) / DAY,
  )
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  return a.year === n.year ? weekdayDay(at, tz) : `${weekdayDay(at, tz)}, ${a.year}`
}

/** Time for today's messages, "Yesterday", or the date — with its year, before this one. */
export function shortWhen(at: string, now: string, tz: string): string {
  const label = dayLabel(at, now, tz)
  if (label === 'Today') return clockAt(at, tz)
  if (label === 'Yesterday') return label
  const year = wallClock(at, tz).year
  return year === wallClock(now, tz).year ? shortDay(at, tz) : `${shortDay(at, tz)}, ${year}`
}

/** Sent by an automatic rule — Monday's reminders and schedule emails included. */
export const isAuto = (m: Pick<Communication, 'note'>) => m.note.startsWith('Automatic')

/** A message on one line: an email's subject, an event's title, else its words — a note's always. */
export const oneLine = (m: Pick<Communication, 'channel' | 'subject' | 'title' | 'body'>) =>
  (m.channel === 'internal_note'
    ? m.body
    : ((m.channel === 'email' ? m.subject : null) ?? m.title ?? m.body)
  ).replace(/\s*\n+\s*/g, ' ')

/** A person's row in the list: their newest text or email, and how many of theirs are unread. */
export function threadOf(
  person: ThreadPerson,
  last: Communication,
  unreadCount: number,
  ctx: { now: string; timeZone: string },
): Thread {
  const who = last.direction === 'inbound' ? '' : isAuto(last) ? 'Auto: ' : 'You: '
  return {
    ...person,
    initials: initials(person.name),
    when: shortWhen(last.at, ctx.now, ctx.timeZone),
    preview: `${who}${oneLine({ ...last, title: null })}`,
    unread: unreadCount > 0,
    unreadCount,
    at: last.at,
  }
}

/** A person's texts and emails, oldest first, under a heading per day. */
export function conversation(
  messages: readonly Communication[],
  ctx: { now: string; timeZone: string },
): { label: string; messages: ConversationMessage[] }[] {
  const days: { label: string; messages: ConversationMessage[] }[] = []
  const sorted = messages
    .filter((m) => m.channel === 'sms' || m.channel === 'email')
    .sort((a, b) => a.at.localeCompare(b.at))
  for (const m of sorted) {
    const label = dayLabel(m.at, ctx.now, ctx.timeZone)
    if (days.at(-1)?.label !== label) days.push({ label, messages: [] })
    const by =
      m.direction === 'inbound'
        ? null
        : isAuto(m)
          ? 'Automatic'
          : m.note
              .replace(/^(Sent|Written) by /, '')
              .split(/ (in|from) the app/)[0]
              ?.split(/ to (all|day|night)/)[0]
    days.at(-1)?.messages.push({
      id: m.id,
      kind: m.channel === 'email' ? 'email' : 'text',
      direction: m.direction === 'inbound' ? 'inbound' : 'outbound',
      subject: m.subject,
      text: m.body,
      time: clockAt(m.at, ctx.timeZone),
      at: m.at,
      channel: m.channel === 'email' ? 'Email' : 'Text',
      by: by || null,
      delivery: m.direction === 'inbound' ? null : deliveryOf(m.status),
      blast: m.purpose === 'staff_broadcast',
      unread: m.direction === 'inbound' && m.readAt === null,
    })
  }
  return days
}
